import {
    AfterViewInit,
    Inject,
    Injector,
    OnDestroy,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import {
    ActivatedRoute,
    Router,
} from '@angular/router';
import { AppStateFacade } from '@app-state';
import {
    Interval,
    ODataResponse,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    FILTER_BUILDER,
    ODataFilterBuilder,
} from '@pages/generics/filter-builder';
import { DEFAULT_TRANSACTION_INTERVALS } from '@pages/shared/chart.config';
import {
    ModalService,
    ODataQueryOptions,
    StorageService,
} from '@services';
import { ODataService } from '@services/generics/odata.service';
import { IGridDataEntry } from '@uipath/angular/components/ui-grid';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';

import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import {
    BehaviorSubject,
    combineLatest,
    forkJoin,
    merge,
    of,
    ReplaySubject,
} from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    startWith,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import {
    GRID_SERVICE,
    GridComponent,
    GridPageState,
    IODataSource,
} from './grid.component';
import {
    GridStateHandler,
    IGridState,
    STATE_HANDLER,
} from './state-handlers';

export abstract class ODataGridComponent<
    TEntity extends IGridDataEntry,
    TKey extends string | number,
    TResponse extends ODataResponse<TEntity> = ODataResponse<TEntity>,
    TQuery extends ODataQueryOptions = ODataQueryOptions,
    > extends GridComponent<TEntity> implements AfterViewInit, OnDestroy {

    public get intervals() {
        return DEFAULT_TRANSACTION_INTERVALS;
    }

    private get _footerState(): PageEvent {
        return this._grid.footer ?
            this._grid.footer.state :
            this._defaultPage;
    }

    private get _storageKey() {
        const buildVersion = this._appState.authConfig.value!.serverSettings.build.version;
        const userId = this._appState.user.value!.id;
        const gridName = location.pathname;
        const tid = this._appState.user.value!.tenancyId || 'host';

        return `${buildVersion}-${userId}-${gridName}-${tid}`;
    }

    public Interval = Interval;

    protected _expand$ = new BehaviorSubject<string[]>([]);
    protected _select$ = new BehaviorSubject<string[]>([]);
    protected _activeQuery$ = new BehaviorSubject<TQuery>({} as TQuery);
    protected _currentState?: IGridState<TEntity>;
    protected _previousState?: IGridState<TEntity>;
    protected _router: Router;

    private _filterBuilder: ODataFilterBuilder<TEntity>;

    constructor(
        @Inject(GRID_SERVICE)
        protected _service: IODataSource<TEntity, TQuery, TResponse>,
        protected _appState: AppStateFacade,
        protected _translate: TranslateService,
        protected _injector: Injector,
        protected _snack: UiSnackBarService,
        protected _modal: ModalService,
        private _storageService: StorageService,
    ) {
        super();
        this._router = this._injector.get<Router>(Router);
        this._filterBuilder = this._injector.get<ODataFilterBuilder<TEntity>>(FILTER_BUILDER, new ODataFilterBuilder<TEntity>());
    }

    public intervalValueFor(interval: Interval) {
        return DEFAULT_TRANSACTION_INTERVALS.find(f => f.value === interval);
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();

        const stateChange$ = new ReplaySubject<any>(1);

        combineLatest([
            this._pagination$!,
            this._grid.sortManager.sort$,
            this._search$!,
            this._aggregatedFilters$!,
            this._expand$,
            this._select$,
        ])
            .pipe(
                map(([
                    page,
                    sort,
                    searchFilters,
                    filters,
                    expand,
                    select,
                ]) => ({
                    page: { pageIndex: page.pageIndex, pageSize: page.pageSize },
                    sort: { direction: sort.direction, field: sort.field },
                    searchFilters,
                    filters,
                    expand,
                    select,
                })),
                distinctUntilChanged((x, y) => isEqual(x, y)),
                tap(state => {
                    const navigateToFirstPage = this._currentState != null &&
                        (
                            !isEqual(this._currentState.filters, state.filters) ||
                            !isEqual(this._currentState.searchFilters, state.searchFilters)
                        );

                    if (navigateToFirstPage) {
                        state.page.pageIndex = 0;
                    }

                    this._previousState = cloneDeep(this._currentState);
                    this._currentState = state;

                    stateChange$.next();
                }),
                takeUntil(this._destroyed$),
            )
            .subscribe();

        const folder$ = this._appState.user.folder.change$
            .pipe(
                tap(_ => {
                    const filtersToReset = this._ouDependentFilters();

                    this._grid.columns$.getValue()
                        .filter(c => filtersToReset.indexOf((c.searchableDropdown || c).property as string) > -1)
                        .forEach(c => c.refetch = true);

                    const independentFilters = this._grid.filterManager.filter$
                        .getValue()
                        .filter(c => filtersToReset.indexOf(c.property as string) === -1);

                    this._grid.filterManager.filter$.next(independentFilters);
                }),
            );

        const refreshTrigger$ = merge(
            this._grid.refresh,
            this._refresh$,
            folder$,
        ).pipe(
            startWith(void 0),
        );

        combineLatest([
            refreshTrigger$,
            this._activeQuery$,
            this._state$,
        ])
            .pipe(
                debounceTime(50),
                filter(([_trigger, _query, state]) => state === GridPageState.ready),
                tap(() => this.loading$.next(true)),
                map(([_, query]) => query),
                switchMap((query) => this._service
                    .get(query)
                    .pipe(
                        catchError(() => {
                            this.loading$.next(false);
                            return of({ total: 0, data: <TEntity[]>[] } as TResponse);
                        }),
                        tap(() => this.loading$.next(false)),
                    ),
                ),
                takeUntil(this._destroyed$),
            )
            .subscribe(({ data, total }) => {
                this.data$.next(data);
                this.total$.next(total);
            });

        const stateHandler = this._injector.get<GridStateHandler<TEntity>>(
            STATE_HANDLER,
            api => api.stateChange$
                .pipe(takeUntil(api.destroyed$))
                .subscribe(() => api.nextQuery()),
        );

        stateHandler({
            appState: this._appState,
            storageService: this._storageService,
            destroyed$: this._destroyed$,
            nextQuery: () => this._activeQuery$.next(this._buildQuery(this._currentState!)),
            pageChange: (p: PageEvent) => this._grid.footer && this._grid.footer.pageChange.next(p),
            columns: this._grid.columns,
            filterManager: this._grid.filterManager,
            header: this._grid.header!,
            pageState: this._footerState,
            sortManager: this._grid.sortManager,
            router: this._router,
            stateChange$,
            storageKey: this._storageKey,
            previousState: () => this._previousState,
            state: () => this._currentState,
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();

        this._activeQuery$.complete();
        this._expand$.complete();
    }

    public resetFilter() {
        const [url] = this._router.url.split('?');
        this._storageService.remove(this._storageKey);

        // ensure we redirect to a static safe path
        // if host '/' redirects to '/host/tenants/' which can be the initial route
        const tempRedirect = this._appState.user.loggedInAsHost ? '/host/license' : '/';
        this._router.navigate([tempRedirect]).then(
            () => this._router.navigate([url], {
                relativeTo: this._injector.get<ActivatedRoute>(ActivatedRoute),
                replaceUrl: true,
            }),
        );
    }

    public removeItems(items: TEntity[]) {
        const ids = (items || this._grid.selectionManager.selected).map(entity => entity.id);

        this._modal.onConfirm(null,
            () => {
                this.loading$.next(true);

                const svc = (this._service as unknown as ODataService<TEntity, TKey>); // FIXME: unknown?

                const requests = ids.map(id =>
                    svc.delete(id as TKey)
                        .pipe(catchError(() => of(true))),
                );

                forkJoin(requests)
                    .pipe(finalize(() => this.loading$.next(false)))
                    .subscribe(() => this._refresh$.next());
            },
            () => { }, this._translate.instant('grid_action_delete_message'));
    }

    protected _ouDependentFilters(): string[] {
        return [];
    }

    protected _buildQuery = ({
        page,
        sort,
        searchFilters,
        filters,
        expand,
        select,
    }: IGridState<TEntity>) => this._filterBuilder.build(
        { ...this._footerState, pageSize: page.pageSize, pageIndex: page.pageIndex },
        { ...this._grid.sortManager.sort$.value, direction: sort.direction, field: sort.field },
        searchFilters,
        filters,
        expand,
        select,
        this._service.mapper!,
    ) as TQuery
}
